import React from 'react';
import { BreadcrumbJsonLd } from 'next-seo';
import he from 'he';
import { IconButton, Link, Typography, cn } from '@kiiwi/ui';
import FontAwesomeIcon from '@/components/atoms/FontawesomeIcon/FontawesomeIcon';

export type BreadcrumbItem = {
  name: string;
  url: string;
};

type Props = {
  className?: string;
  items: BreadcrumbItem[];
};

const Breadcrumbs = ({ items, className }: Props) => {
  if (items.length === 0) return null;

  const linkItems = items.map((item, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={`breadcrumb-${index}`}>
      {index !== 0 && (
        <IconButton size="sm" className="pointer-events-none mx-1 h-4 w-4">
          <FontAwesomeIcon
            name="angle-right:regular"
            className="h-4 w-4 fill-[var(--kiiwi-carbon-600)]"
          />
        </IconButton>
      )}
      {index === items.length - 1 ? (
        <Typography variant="body-1" className="text-neutral-primary">
          {item.name}
        </Typography>
      ) : (
        <Link
          key={item.url}
          href={item.url}
          className="text-[var(--kiiwi-indigo-400)] underline visited:text-picker-indigo hover:no-underline active:text-picker-indigo"
        >
          {he.decode(item.name)}
        </Link>
      )}
    </React.Fragment>
  ));

  return (
    <div className={cn('flex w-full flex-wrap items-center', className)}>
      {linkItems}

      <BreadcrumbJsonLd
        itemListElements={items.map((item, index) => ({
          position: index,
          name: he.decode(item.name),
          item: item.url,
        }))}
      />
    </div>
  );
};

export default React.memo(
  Breadcrumbs,
  (oldProps, newProps) => oldProps.items === newProps.items,
);
